import { Box, Typography } from "@mui/material";

export function DailyCalendar({
  year,
  month,
  data,
  onPrev: _onPrev,
  onNext: _onNext,
  onDayClick,
}: {
  year: number;
  month: number;
  data: { name: string; 合計: number }[];
  onPrev: () => void;
  onNext: () => void;
  onDayClick?: (day: number) => void;
}) {
  const today = new Date();
  const todayDate =
    today.getFullYear() === year && today.getMonth() + 1 === month
      ? today.getDate()
      : -1;
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const countMap: { [day: number]: number } = {};
  data.forEach((d) => {
    // "YYYY年M月D日" 形式または純粋な数字文字列（例: "8"）から日を抽出
    const dayMatch = d.name.match(/^(\d+)$|(\d+)日/);
    const day = dayMatch ? parseInt(dayMatch[1] ?? dayMatch[2], 10) : NaN;
    if (!isNaN(day) && day >= 1 && day <= 31) countMap[day] = d.合計;
  });
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  const dayHeaders = ["日", "月", "火", "水", "木", "金", "土"];
  return (
    <Box data-testid="daily-calendar">
      <Box sx={{ display: "flex", alignItems: "center", mb: 1, justifyContent: "center" }}>
        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
          {year}年{String(month).padStart(2, "0")}月
        </Typography>
      </Box>
      <Box data-testid="daily-calendar-grid" sx={{ border: "1px solid var(--color-border)", borderRadius: 1, overflow: "hidden" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", bgcolor: "var(--color-ai-pale)" }}>
          {dayHeaders.map((h, i) => (
            <Box
              key={h}
              className={`cal-th${i === 0 ? " wd-sun" : i === 6 ? " wd-sat" : ""}`}
              sx={{
                textAlign: "center",
                py: 0.5,
                fontSize: "var(--fs-11)",
                fontWeight: "var(--fw-semibold)",
                color: i === 0 ? "var(--sem-error)" : i === 6 ? "var(--sem-info)" : "var(--color-ai)",
              }}
            >
              {h}
            </Box>
          ))}
        </Box>
        {weeks.map((week, wi) => (
          <Box key={wi} sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
            {week.map((day, di) => {
              if (day === null) {
                return (
                  <Box
                    key={di}
                    sx={{
                      borderRight: "1px solid var(--color-border-light)",
                      borderBottom: "1px solid var(--color-border-light)",
                      minHeight: 44,
                    }}
                  />
                );
              }
              const isToday = day === todayDate;
              const count = countMap[day] ?? 0;
              const clickable = !!onDayClick;
              return (
                <Box
                  key={di}
                  className={isToday ? "cal-today" : undefined}
                  onClick={clickable ? () => onDayClick(day) : undefined}
                  sx={{
                    borderRight: "1px solid var(--color-border-light)",
                    borderBottom: "1px solid var(--color-border-light)",
                    minHeight: 44,
                    p: 0.3,
                    textAlign: "center",
                    bgcolor: isToday ? "var(--color-kohaku-pale)" : undefined,
                    outline: isToday ? "2px solid var(--color-kohaku)" : undefined,
                    outlineOffset: isToday ? "-2px" : undefined,
                    cursor: clickable ? "pointer" : undefined,
                    "&:hover": clickable
                      ? { bgcolor: isToday ? "rgba(184,134,11,0.1)" : "action.hover" }
                      : undefined,
                  }}
                >
                  <Typography
                    className="cal-d"
                    sx={{
                      display: "block",
                      fontSize: "var(--fs-11)",
                      fontWeight: isToday ? "var(--fw-bold)" : undefined,
                      color: isToday ? "var(--color-kohaku-text)" : "var(--color-sumi)",
                      lineHeight: 1.2,
                    }}
                  >
                    {day}
                  </Typography>
                  <Typography
                    className={`cal-n${count === 0 ? " zero" : ""}`}
                    sx={{
                      display: "block",
                      fontSize: "var(--fs-10)",
                      fontWeight: count === 0 ? "var(--fw-regular)" : "var(--fw-bold)",
                      color: count === 0 ? "var(--color-sumi-muted)" : "var(--cat-entry-text)",
                      opacity: count === 0 ? 0.4 : undefined,
                      lineHeight: 1.2,
                    }}
                  >
                    {count}件
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
